import React, { useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  Info,
  FileText,
  PlusCircle,
  MinusCircle,
  ClipboardList,
  Ban,
  HelpCircle,
  List,
} from "lucide-react";
import EditableInfo from "./EditableInfo";
import DynamicInputList from "../forms/DynamicInputList";

/**
 * Field configuration for General Info section
 * Separating config from logic for better maintainability
 */
const getFieldsConfig = (t, product) => [
  {
    key: "shortSummary",
    title: t("introduce_activity"),
    caption: t("introduce_activity_caption"),
    icon: <Info size={24} color="#8077EC" />,
    type: "textarea",
    modelId: product.shortSummary?.[0]?.id || null,
    value: product.shortSummary?.[0]?.value || "",
  },
  {
    key: "descriptions",
    title: t("add_detailed_description"),
    caption: t("add_detailed_description_caption"),
    icon: <FileText size={24} color="#8077EC" />,
    type: "textarea",
    modelId: product.descriptions?.[0]?.id || null,
    value: product.descriptions?.[0]?.value || "",
  },
  {
    key: "keyPoints",
    title: t("resume_highlights"),
    caption: t("resume_highlights_caption"),
    icon: <List size={24} color="#8077EC" />,
    type: "dynamic-list",
  },
  {
    key: "included",
    title: t("included"),
    caption: t("included_caption"),
    icon: <PlusCircle size={24} color="#8077EC" />,
    type: "dynamic-list",
  },
  {
    key: "excluded",
    title: t("excluded"),
    caption: t("excluded_caption"),
    icon: <MinusCircle size={24} color="#8077EC" />,
    type: "dynamic-list",
  },
  {
    key: "requiredItems",
    title: t("activity2Contr3"),
    caption: t("activity2Contr3p"),
    icon: <ClipboardList size={24} color="#8077EC" />,
    type: "dynamic-list",
  },
  {
    key: "prohibitedItems",
    title: t("please_fill_prohibited_items"),
    caption: t("please_fill_prohibited_items"),
    icon: <Ban size={24} color="#8077EC" />,
    type: "dynamic-list",
  },
  {
    key: "otherInformations",
    title: t("activity2Contr4"),
    caption: t("activity2Contr4p"),
    icon: <HelpCircle size={24} color="#8077EC" />,
    type: "dynamic-list",
  },
];

/**
 * Format value for display based on field type
 */
const formatDisplayValue = (value, type) => {
  if (!value) return "—";
  if (type === "dynamic-list" && Array.isArray(value)) {
    if (value.length === 0) return "—";
    return (
      <ol className="m-0 ps-3">
        {value.map((item, index) => (
          <li key={index} className="text-dark-purple">
            {typeof item === "object" ? item?.value : item}
          </li>
        ))}
      </ol>
    );
  }
  if (type === "textarea") return value[0]?.value;

  return value;
};

/**
 * Get input component for editing based on field type
 */
const getEditComponent = (field, product) => {
  switch (field.type) {
    case "dynamic-list":
      return (
        <DynamicInputList
          initialValues={product[field.key] || []}
          productId={product.id}
          model={field.key}
        />
      );
    case "textarea":
      return <textarea className="form-control w-100" rows={4} />;
    default:
      return field.component;
  }
};

/**
 * GeneralInfo Component - Activity general information management
 */
const GeneralInfo = ({ editing, product, onSaveSuccess }) => {
  const { t } = useTranslation();
  const fieldsConfig = getFieldsConfig(t, product);

  // Memoized success callback
  const handleSaveSuccess = useCallback(
    (fieldKey) => {
      console.log(`✅ Field updated: ${fieldKey}`);
      if (onSaveSuccess) onSaveSuccess(fieldKey);
    },
    [onSaveSuccess]
  );

  return (
    <Row>
      {fieldsConfig.map((field) => {
        const displayValue = formatDisplayValue(product[field.key], field.type);

        return (
          <Col sm="12" key={field.key} className="mb-4">
            <EditableInfo
              title={field.title}
              caption={field.caption}
              icon={field.icon}
              value={displayValue}
              fieldKey={field.key}
              productId={product.id}
              modelId={field.modelId}
              onSaveSuccess={() => handleSaveSuccess(field.key)}
              fieldType={field.type}
            >
              {getEditComponent(field, product)}
            </EditableInfo>
            <hr
              style={{ borderTop: "1.5px solid #9ec5fe", margin: "2rem 0" }}
            />
          </Col>
        );
      })}
    </Row>
  );
};

export default GeneralInfo;
